import { useContractMasterDataApi } from "@/features/masterdata/api/useContractMasterDataApi";
import { SupplierFull } from "@/types/ContractMasterData";
import { useQuery } from "@tanstack/vue-query";
import { datadogRum } from "@datadog/browser-rum";

export const SUPPLIERS_PAGE_SIZE = 1000;
export const MAX_SUPPLIER_PAGES = 50;

const INTERNAL_SUPPLIERS_FILTER = 'type=="INTERNAL"';
const THIRD_PARTY_SUPPLIERS_FILTER = 'type=="THIRD_PARTY"';

/**
 * Maps an experience source to the supplier `type` RSQL filter.
 *
 * - `INTERNAL` (OPINOIA): internal users for own offer and 3rd party -> internal suppliers.
 * - `NOVA` (Manual): internal users creating 3rd party products -> 3rd-party suppliers.
 * - Any other source (or none) is not restricted.
 */
function getSupplierTypeFilter(experienceSource: string | undefined): string | undefined {
  switch (experienceSource) {
    case "INTERNAL":
      return INTERNAL_SUPPLIERS_FILTER;
    case "NOVA":
      return THIRD_PARTY_SUPPLIERS_FILTER;
    default:
      return undefined;
  }
}

/**
 * Provides the suppliers masterdata.
 *
 * When an `experienceSource` is provided, the list is filtered by supplier type
 * to match the experience (see {@link getSupplierTypeFilter}). When it is omitted
 * the query stays unfiltered and returns all suppliers, which is how the details
 * lookup resolves any supplier by id (so existing factsheets keep displaying their
 * supplier regardless of type).
 */
export function useSuppliersMasterdataQuery(options: { experienceSource?: Ref<string | undefined> } = {}) {
  const { getSuppliers } = useContractMasterDataApi();
  const { experienceSource } = options;
  const filters = computed(() => getSupplierTypeFilter(experienceSource?.value));
  const runtimeConfig = useRuntimeConfig();

  return useQuery({
    queryKey: ["suppliers-masterdata", experienceSource],
    queryFn: trackLoadAll,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  /**
   * Times the whole "load all suppliers" operation end to end and reports it as
   * a `suppliers.load_all` RUM action, so we can watch the user-facing latency
   * of the paginated fetch. Logs to the console locally (Datadog is disabled on
   * dev) for quick verification.
   */
  async function trackLoadAll(): Promise<SupplierFull[]> {
    const start = performance.now();
    try {
      return await fetchAllSuppliers();
    } finally {
      const durationMs = Math.round(performance.now() - start);
      datadogRum.addAction("suppliers.load_all", { durationMs });

      if (runtimeConfig.public.DISABLE_DATADOG) {
        // eslint-disable-next-line no-console
        console.log("[suppliers.load_all]", durationMs, "ms");
      }
    }
  }

  /**
   * Fetches every supplier from the paginated `GET /suppliers` endpoint.
   *
   * Fetches the first page to learn the total from the `total-count` header,
   * then requests all remaining pages in parallel and concatenates them in
   * page order. This restores the "full list" contract the list consumers rely
   * on now that the endpoint is paginated.
   */
  async function fetchAllSuppliers(): Promise<SupplierFull[]> {
    const fetchPage = (page: number) =>
      getSuppliers(
        { filters: filters.value },
        { config: {}, limit: SUPPLIERS_PAGE_SIZE, offset: page * SUPPLIERS_PAGE_SIZE }
      );

    const first = await fetchPage(0);
    const firstItems = first.data ?? [];

    // A short first page is the only page, regardless of the header.
    if (firstItems.length < SUPPLIERS_PAGE_SIZE) {
      return firstItems;
    }

    // The first page is full, so more suppliers exist, but without a usable
    // total-count we cannot know how many pages to request in parallel.
    const totalCount = Number(first.headers?.["total-count"] ?? 0);
    if (!totalCount) {
      throw new Error("Cannot paginate suppliers: missing or invalid total-count header");
    }

    const totalPages = Math.min(Math.ceil(totalCount / SUPPLIERS_PAGE_SIZE), MAX_SUPPLIER_PAGES);

    // The header reports the first page already holds everything.
    if (totalPages <= 1) {
      return firstItems;
    }

    // Fetch the remaining pages (1..totalPages-1) in parallel. `total-count`
    // can be stale and overstate the number of pages, so we settle every
    // request rather than letting a single rejection fail the batch.
    const settled = await Promise.allSettled(Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 1)));

    // Walk the pages in order and stop at the first short page: once a page
    // comes back short, the list is complete and every later page is a request
    // for data that does not exist (so its result or failure is irrelevant).
    // Until that point every page is required, so a rejection is fatal.
    const all = [...firstItems];

    for (const result of settled) {
      if (result.status === "rejected") {
        throw result.reason;
      }

      const pageItems = result.value.data ?? [];
      all.push(...pageItems);

      if (pageItems.length < SUPPLIERS_PAGE_SIZE) break;
    }

    return all;
  }
}
